import { useEffect, useCallback, useRef } from 'react'
import { useChatStore, MessageStatus, ChatMessage } from '@/store/chatStore'
import { chatApi } from '@/services/api'
import { wsService, WebSocketMessage } from '@/services/websocket'

/**
 * Play base64-encoded MP3 audio
 */
function playAudioFromBase64(base64Audio: string): void {
  try {
    const audioData = atob(base64Audio)
    const arrayBuffer = new ArrayBuffer(audioData.length)
    const view = new Uint8Array(arrayBuffer)
    for (let i = 0; i < audioData.length; i++) {
      view[i] = audioData.charCodeAt(i)
    }
    const blob = new Blob([arrayBuffer], { type: 'audio/mpeg' })
    const url = URL.createObjectURL(blob)
    const audio = new Audio(url)
    audio.onended = () => URL.revokeObjectURL(url)
    audio.play().catch((err) => console.error('[Audio] Playback failed:', err))
  } catch (error) {
    console.error('[Audio] Failed to decode/play audio:', error)
  }
}

export function useChat() {
  const {
    messages,
    conversations,
    currentConversationId,
    isTyping,
    error,
    addMessage,
    updateMessage,
    setMessages,
    setConversations,
    setCurrentConversation,
    setTyping,
    setError,
    clearMessages,
  } = useChatStore()

  const pendingMessageRef = useRef<string | null>(null)

  // Track recently sent messages to prevent duplicates (within 2 seconds)
  const recentlySentRef = useRef<Set<string>>(new Set())

  // Track if we've received an assistant response for the current message
  const waitingForResponseRef = useRef(false)

  // Track processed message hashes to prevent duplicates
  const processedMessagesRef = useRef<Set<string>>(new Set())

  // Handle incoming WebSocket messages
  useEffect(() => {
    const unsubscribeChat = wsService.on('chat', (message: WebSocketMessage) => {
      const data = message.data as {
        role?: string
        content?: string
        status?: string
        conversation_id?: string
        metadata?: Record<string, unknown>
        audio_base64?: string
      }

      if (data.role === 'assistant' && data.status !== 'received') {
        // Play audio response if available
        if (data.audio_base64) {
          console.log('[Chat] Playing audio response')
          playAudioFromBase64(data.audio_base64)
        }
        // Only process if we're waiting for a response (prevents duplicates)
        if (!waitingForResponseRef.current) return

        // Create a hash of the message content to prevent duplicates
        const contentHash = `${data.role}-${data.content?.slice(0, 100)}-${data.conversation_id}`
        if (processedMessagesRef.current.has(contentHash)) {
          console.warn('[useChat] Skipping duplicate assistant message')
          return
        }
        processedMessagesRef.current.add(contentHash)

        // Clear old hashes after 10 seconds
        setTimeout(() => {
          processedMessagesRef.current.delete(contentHash)
        }, 10000)

        waitingForResponseRef.current = false

        setTyping(false)

        // Update pending message status
        if (pendingMessageRef.current) {
          updateMessage(pendingMessageRef.current, {
            status: 'delivered' as MessageStatus,
          })
          pendingMessageRef.current = null
        }

        // Add assistant response with metadata
        addMessage({
          role: 'assistant',
          content: data.content || '',
          status: 'delivered',
          conversationId: data.conversation_id,
          metadata: data.metadata as ChatMessage['metadata'],
        })

        // Update conversation ID if provided (don't clear messages for new conversation)
        if (data.conversation_id && !currentConversationId) {
          setCurrentConversation(data.conversation_id, false)
        }
      } else if (data.role === 'user' && data.status === 'received') {
        // User message acknowledged - start waiting for response
        if (pendingMessageRef.current) {
          updateMessage(pendingMessageRef.current, {
            status: 'sent' as MessageStatus,
          })
        }
        waitingForResponseRef.current = true
        setTyping(true)
      }
    })

    const unsubscribeError = wsService.on('error', (message: WebSocketMessage) => {
      setTyping(false)
      const errorData = message.data as { error?: string }
      setError(errorData.error || 'An error occurred')

      if (pendingMessageRef.current) {
        updateMessage(pendingMessageRef.current, {
          status: 'error' as MessageStatus,
        })
        pendingMessageRef.current = null
      }
    })

    return () => {
      unsubscribeChat()
      unsubscribeError()
    }
  }, [addMessage, updateMessage, setTyping, setError, currentConversationId, setCurrentConversation])

  interface Attachment {
    file_id: string
    filename: string
    content_type: string
    size: number
    is_image: boolean
    preview?: string
  }

  interface SendMessageOptions {
    isVoiceInput?: boolean
    voice?: string
  }

  const sendMessage = useCallback(
    async (content: string, attachments?: Attachment[], options?: SendMessageOptions) => {
      if (!content.trim() && (!attachments || attachments.length === 0)) return


      // Prevent duplicate sends within 2 seconds (fixes double STK push issue)
      const messageHash = content + (attachments?.length || 0)
      const recentHash = messageHash
      
      if (recentlySentRef.current.has(recentHash)) {
        console.warn('[useChat] Prevented duplicate message send within 2 seconds')
        return
      }
      
      recentlySentRef.current.add(recentHash)
      setTimeout(() => {
        recentlySentRef.current.delete(recentHash)
      }, 2000)

      // Build message content with attachment info
      let displayContent = content.trim()
      if (attachments && attachments.length > 0) {
        const attachmentText = attachments.map(a =>
          a.is_image ? `📷 ${a.filename}` : `📎 ${a.filename}`
        ).join(', ')
        if (displayContent) {
          displayContent = `${displayContent}\n\n[Attached: ${attachmentText}]`
        } else {
          displayContent = `[Attached: ${attachmentText}]`
        }
      }

      // Add message to store with sending status
      const messageId = addMessage({
        role: 'user',
        content: displayContent,
        status: 'sending',
        conversationId: currentConversationId || undefined,
      })

      pendingMessageRef.current = messageId

      // Extract file IDs for the backend
      const fileIds = attachments?.map(a => a.file_id)

      try {
        // Send via WebSocket for real-time with conversation context
        wsService.sendChat(
          content.trim() || 'Please analyze the attached file(s)',
          currentConversationId || undefined,
          fileIds,
          {
            isVoiceInput: options?.isVoiceInput,
            voice: options?.voice,
          }
        )
      } catch (err) {
        // Fallback to REST API
        try {
          const response = await chatApi.sendMessage(
            content.trim() || 'Please analyze the attached file(s)',
            currentConversationId || undefined
          )

          updateMessage(messageId, { status: 'delivered' })

          // Add assistant response
          addMessage({
            role: 'assistant',
            content: response.response,
            status: 'delivered',
            conversationId: response.conversation_id,
          })

          if (!currentConversationId && response.conversation_id) {
            setCurrentConversation(response.conversation_id, false)
          }
        } catch (apiError) {
          updateMessage(messageId, { status: 'error' })
          setError('Failed to send message')
        }
      }
    },
    [addMessage, updateMessage, currentConversationId, setCurrentConversation, setError]
  )

  const loadConversations = useCallback(async () => {
    try {
      const data = await chatApi.getConversations()
      setConversations(data)
    } catch (err) {
      console.error('Failed to load conversations:', err)
    }
  }, [setConversations])

  const loadConversation = useCallback(
    async (conversationId: string) => {
      try {
        const data = await chatApi.getConversation(conversationId)
        setCurrentConversation(conversationId)
        setMessages(
          data.messages.map((msg: { id: string; role: string; content: string; created_at: string }) => ({
            id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: new Date(msg.created_at),
            status: 'delivered' as MessageStatus,
            conversationId,
          }))
        )
      } catch (err) {
        console.error('Failed to load conversation:', err)
        setError('Failed to load conversation')
      }
    },
    [setCurrentConversation, setMessages, setError]
  )

  const retryMessage = useCallback(
    (messageId: string) => {
      const message = messages.find((m) => m.id === messageId)
      if (message && message.status === 'error') {
        updateMessage(messageId, { status: 'sending' })
        pendingMessageRef.current = messageId
        wsService.sendChat(message.content)
      }
    },
    [messages, updateMessage]
  )

  return {
    messages,
    conversations,
    currentConversationId,
    isTyping,
    error,
    sendMessage,
    loadConversations,
    loadConversation,
    retryMessage,
    clearMessages,
    setError,
  }
}
